import { ChangeDetectionStrategy, Component, computed, input, InputSignal, OnInit, signal, Signal, WritableSignal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MENU_ITEMS, MenuItem } from 'Core/constants';
import { getUserRoutes } from 'Core/helpers';
import { AppRoutes } from 'Core/routes';
import { CurrentUserStateService } from 'Core/services';
import { filter } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SidenavComponent implements OnInit {
  public readonly isCollapsed: InputSignal<boolean> = input.required<boolean>();
  public readonly menuItems: Readonly<MenuItem[]> = MENU_ITEMS;
  public readonly homeRoute: Signal<string> = computed(() => {
    const user = this.currentUserStateService.user();
    return user ? `/${getUserRoutes(user)}` : `/${AppRoutes.APP}`;
  });
  /** Логотип компании вместо дефолтного; битую ссылку запоминаем, чтобы откатиться на дефолт */
  protected readonly brokenLogoUrl: WritableSignal<string | null> = signal<string | null>(null);
  protected readonly companyLogoUrl: Signal<string | null> = computed((): string | null => {
    const logoUrl: string | null = this.currentUserStateService.user()?.company?.logoUrl ?? null;
    return logoUrl && logoUrl !== this.brokenLogoUrl() ? logoUrl : null;
  });
  protected readonly companyName: Signal<string | undefined> = computed(
    (): string | undefined => this.currentUserStateService.user()?.company?.shortName?.trim() || undefined
  );
  public currentPath: string = '';
  public activeSubmenuParents: Record<string, boolean> = {};

  constructor(
    private readonly router: Router,
    private readonly currentUserStateService: CurrentUserStateService
  ) {}

  public ngOnInit(): void {
    this.syncCurrentPath(this.router.url);
    this.subscribeOnRouteChanges();
  }

  private subscribeOnRouteChanges(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        untilDestroyed(this)
      )
      .subscribe(event => this.syncCurrentPath((event as NavigationEnd).urlAfterRedirects));
  }

  private syncCurrentPath(url: string): void {
    this.currentPath = this.normalizePath(url);
    this.activeSubmenuParents = this.getActiveSubmenuParents(this.currentPath);
  }

  private getActiveSubmenuParents(currentPath: string): Record<string, boolean> {
    return this.menuItems.reduce<Record<string, boolean>>((acc, menuItem) => {
      if (!menuItem.submenuItems?.length) {
        return acc;
      }

      const hasActiveSubmenu = menuItem.submenuItems.some(submenuItem =>
        this.pathMatches(currentPath, this.normalizePath(submenuItem.routerLink))
      );

      if (hasActiveSubmenu) {
        acc[menuItem.routerLink] = true;
      }

      return acc;
    }, {});
  }

  private normalizePath(path: string): string {
    const [pathWithoutQuery] = path.split(/[?#]/);
    const cleanedPath = pathWithoutQuery.replace(/^\/+|\/+$/g, '');
    return cleanedPath.replace(/^app\/?/, '');
  }

  private pathMatches(currentPath: string, routePath: string): boolean {
    return currentPath === routePath || currentPath.startsWith(`${routePath}/`);
  }
}
